/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import Options from '@linc-technologies/linced-common/enums/options';
import PageType from 'linced-app/enums/page-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import EditPostMixin from '@linc-technologies/linced-common/mixins/edit-post';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';
import StaffAccessLevel from '@linc-technologies/linced-common/enums/roles/staff-page-access';
import {
  getTagCountsForPage,
  getTagPieData,
} from '@linc-technologies/linced-common/utils/tags';

export default class PageController extends Controller.extend(EditPostMixin) {
  // Attrs
  PageType = PageType;

  @alias('model.person')
  person;

  @optionAlias(Options.REPORTING_SHOW_SUBJECTS_ON)
  showSubjectsOn;

  // Services
  @service
  options;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  // Props
  // TODO: list the inputs from the route

  StaffAccessLevel = StaffAccessLevel;

  // State
  @tracked numGradeChanges = 0;

  // pageDetailsOpen enables opening and closing the page details modal dialog.
  pageDetailsOpen = false;

  numberOfAssessmentTerms = 4;

  init() {
    super.init(...arguments);
    this.isMobile = isMobile();
  }

  // Methods
  @computed('model.{page.hasAssessments,assessments.length}')
  get assessment() {
    if (
      this.model.page.get('hasAssessments') &&
      this.model.page.get('assessments.length') === 1
    ) {
      // assessments is a plain array; Ember get() has no [0] path syntax.
      return this.model.page.assessments[0];
    }
  }

  @computed('assessment.id')
  get assessmentModules() {
    if (!isEmpty(this.assessment)) {
      return this.store.query('assessments/module', {
        assessmentId: this.assessment.id,
      });
    }
  }

  @computed('model.posts.@each.assessment_id')
  get unlinkedPosts() {
    return this.model.posts.filter((item) => !item.assessment_id);
  }

  @computed('assessment.id', 'model.person.id', 'numGradeChanges')
  get latestGrades() {
    if (!isEmpty(this.assessment)) {
      let filter = {
        personId: this.model.person.id,
        assessmentId: this.assessment.id,
        latest: true,
      };
      return this.store.query('assessments/grade', filter);
    }
  }

  @computed('showSubjectsOn')
  get showSubjects() {
    if (isEmpty(this.showSubjectsOn)) {
      // Show by default if the option isn't set yet.
      return true;
    }
    return ['pages', 'both'].includes(this.showSubjectsOn);
  }

  // canManagePermissions determines whether to show the Page Permission
  // management UI to the current user.
  @computed('model.{page.type,person.id}', 'session.{personId,scopes.[]}')
  get canManagePermissions() {
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // Allow LINC-ED staff to see/manage Page Permissions for pages.
      return true;
    }
    if (this.model.page.type === PageType.STUDENT) {
      // Only staff members with page access level "Access Control Level" can manage permissions.
      return (
        this.session.person.pageAccessLevel ===
        StaffAccessLevel.ACCESS_CONTROL_LEVEL
      );
    }
    // For Staff pages, allow management of only one's own Page Permissions.
    return this.get('model.person.id') === this.get('session.personId');
  }

  // staffCanView calculates whether the current user can view posts on this page,
  // if the page in question is a Staff page.
  @computed('canManagePermissions', 'model.{person.id,pagePermissions.[]}')
  get staffCanView() {
    if (this.canManagePermissions) {
      // If you can manage the permissions, you can also view the Page.
      return true;
    }
    // If it's not your own page, you'll only get any PagePermissions if you
    // have been given access to this Page for this Person.
    return !isEmpty(this.model.pagePermissions);
  }

  @computed('model.{page.tagCategories.[],posts.@each.tags}')
  get tagCounts() {
    return getTagCountsForPage(this.model.page, this.model.posts);
  }

  @tracked
  printDialogOpen = false;

  @tracked
  isMobile;

  @action
  refresh() {
    this.get('model.posts').update();
  }

  @action
  getTagPieData() {
    return getTagPieData.call(this, ...arguments);
  }

  @action
  incrementGradeChanges() {
    this.incrementProperty('numGradeChanges');
  }

  @action
  printPage() {
    this.printDialogOpen = true;
  }

  @action
  closePrintDialog() {
    this.printDialogOpen = false;
  }

  @action
  shouldDisablePostActions(post) {
    if (this.model.page.type !== PageType.STAFF) {
      // Don't mess with the actions on non-staff pages.
      return false;
    }
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // Allow LINC-ED staff to manage all posts.
      return false;
    }

    // Only the author should be able to edit/delete/etc a given post.
    return post.author_id !== this.session.personId;
  }
}
